import { AfterViewInit, Directive, ElementRef, Host, Input, Renderer2, ViewContainerRef } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { delay, distinctUntilChanged, filter, map, startWith, switchMap, tap } from 'rxjs';
import { AbstractListViewService } from '../abstract-list-view.service';
import { MasterListViewComponent } from '../master-list-view.component';

/**
 * Used to dynamically set the height of the list-view-component based on the number of rows.
 *
 * Note: needs `suppressRowVirtualisation` to be set to true on the gridOptions
 *  to prevent issues with more than 10 rows.
 */
@UntilDestroy()
@Directive({
  selector: '[y42DynamicGridHeight]',
  standalone: true,
})
export class DynamicGridHeightDirective implements AfterViewInit {
  constructor(
    @Host() private readonly masterlistViewComponent: MasterListViewComponent,
    private readonly listViewService: AbstractListViewService,
    private readonly container: ViewContainerRef,
    private readonly elementRef: ElementRef,
    private readonly renderer: Renderer2,
  ) {}

  readonly minHeight = 130;
  readonly maxHeight = 500;

  @Input('y42DynamicGridHeight') enabled: string | boolean = true;

  ngAfterViewInit() {
    if (this.enabled) {
      this.setGridHeightOnRowCountChange().pipe(untilDestroyed(this)).subscribe();
      this.setGridHeight();
    }
  }

  setGridHeightOnRowCountChange() {
    return this.masterlistViewComponent.gridReady.pipe(
      map((gridOptions) => gridOptions.api),
      filter(Boolean),
      switchMap(() =>
        this.listViewService.data$.pipe(
          map((data) => data.length),
          distinctUntilChanged(),
          startWith(null),
          delay(0),
          tap(() => this.setGridHeight()),
        ),
      ),
    );
  }

  setGridHeight(height?: number) {
    const containerElement = this.container.element.nativeElement as Element;
    const rowsElements = containerElement.querySelectorAll<HTMLElement>('.ag-row');
    const headerElement = containerElement.querySelector<HTMLElement>('.ag-header');
    const listViewToolbarElement = containerElement.querySelector<HTMLElement>('.list-view-toolbar');

    const gridHeight = height ?? this.getGridHeight(rowsElements, headerElement, listViewToolbarElement);
    this.renderer.setStyle(this.elementRef.nativeElement, 'height', `${gridHeight}px`);
  }

  getGridHeight(
    rowElements: NodeListOf<HTMLElement>,
    headerElement: HTMLElement | null,
    toolbarElement: HTMLElement | null,
  ): number {
    if (rowElements.length === 0) {
      return this.minHeight;
    }

    const totalRowsHeight = Array.from(rowElements).reduce((acc, element) => acc + element.offsetHeight, 0);
    const gridHeaderHeight = headerElement?.offsetHeight ?? 0;
    const toolbarMargin = toolbarElement
      ? parseInt(getComputedStyle(toolbarElement).marginTop) + parseInt(getComputedStyle(toolbarElement).marginBottom)
      : 0;
    const listViewToolbarHeight = (toolbarElement?.offsetHeight ?? 0) + toolbarMargin;
    const targetHeight = listViewToolbarHeight + gridHeaderHeight + totalRowsHeight;
    const targetHeightBoundedFromBelow = Math.max(targetHeight, this.minHeight);

    return Math.min(targetHeightBoundedFromBelow, this.maxHeight);
  }
}
